import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import uploadIcon from 'assets/icons/line-assigments/upload.svg';
import uploadedIcon from 'assets/icons/line-assigments/uploaded.svg';
import { useRef } from 'react';
import { boxDocumentStyles } from '../styles/documentItemStyles';

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

export const DocumentItem = ({
  document,
  createUrl,
  removeDocument,
  isLoadingDocument,
  selectedFile,
  activeTypeDocumentId,
  activeDocumentAction,
  pendingFileName,
}) => {
  const inputRef = useRef(null);

  const hasFile = Boolean(selectedFile);
  const fileUrl = selectedFile?.url || '';
  const fileName = selectedFile?.file?.name || selectedFile?.fileName || '';
  const isActiveDocument = activeTypeDocumentId === document.id;
  const isUploading = Boolean(
    isLoadingDocument && isActiveDocument && activeDocumentAction === 'upload',
  );
  const isRemoving = Boolean(
    isLoadingDocument && isActiveDocument && activeDocumentAction === 'remove',
  );
  const isBusy = isUploading || isRemoving;
  const uploadedDocumentId = selectedFile?.documentId;

  const handleOpenPicker = () => {
    if (isLoadingDocument) return;

    if (hasFile && fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (hasFile) return;

    inputRef.current?.click();
  };

  const handleFileChange = (event) => {
    if (hasFile || isLoadingDocument) {
      event.target.value = '';
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isValidSize = file.size <= MAX_FILE_SIZE_BYTES;

    if (!isPdf || !isValidSize) {
      event.target.value = '';
      return;
    }

    createUrl({
      document_type_id: document.id,
      file_name: file.name,
      content_type: 'application/pdf',
      file,
    });

    event.target.value = '';
  };

  const handleRemoveFile = (event) => {
    event.stopPropagation();

    if (isLoadingDocument || !hasFile) {
      return;
    }

    removeDocument(document.id, uploadedDocumentId);
  };

  return (
    <Box sx={{ width: '100%' }} mt={2}>
      <Typography
        sx={{
          mb: 1,
          fontSize: '14px',
          lineHeight: 1.3,
          color: '#212121',
          fontWeight: 600,
        }}
      >
        {document.name}
      </Typography>

      <Box
        role='button'
        tabIndex={0}
        onClick={handleOpenPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenPicker();
          }
        }}
        sx={boxDocumentStyles(isBusy, hasFile)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, pr: 1 }}>
          {isBusy ? (
            <CircularProgress size={24} sx={{ mr: 1.5, flexShrink: 0 }} />
          ) : (
            <Box
              component='img'
              src={hasFile ? uploadedIcon : uploadIcon}
              alt='estado archivo'
              sx={{ width: 26, height: 26, mr: 1.5, flexShrink: 0 }}
            />
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: 1.2,
                color: '#4a4a4a',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isUploading
                ? pendingFileName || 'Subiendo archivo...'
                : isRemoving
                  ? 'Eliminando archivo...'
                  : hasFile
                    ? fileName
                    : 'Subir PDF'}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: '12px', color: '#9e9e9e', lineHeight: 1.2 }}>
              {isBusy
                ? 'Por favor espera mientras se procesa el documento'
                : 'Tamaño máximo: 100MB'}
            </Typography>
          </Box>
        </Box>

        {isBusy ? (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#6d6d6d',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              ml: 1,
              flexShrink: 0,
            }}
          >
            {isUploading ? 'Subiendo...' : 'Eliminando...'}
          </Typography>
        ) : hasFile ? (
          <IconButton
            size='small'
            aria-label='Eliminar archivo'
            onClick={handleRemoveFile}
            disabled={isLoadingDocument}
            sx={{ ml: 1, color: '#8f8f8f', flexShrink: 0 }}
          >
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        ) : (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#b5b5b5',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              ml: 1,
              flexShrink: 0,
            }}
          >
            Ningún archivo seleccionado
          </Typography>
        )}
      </Box>

      <input
        ref={inputRef}
        type='file'
        accept='application/pdf,.pdf'
        hidden
        onChange={handleFileChange}
      />
    </Box>
  );
};
